import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FormData {
    // User account
    name: string;
    email: string;
    password: string;
    password_confirmation: string;

    // Business info
    business_name: string;
    business_type: string;
    industry: string;
    registration_number: string;

    // Contact info
    phone: string;
    website: string;
    address_street: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
    address_country: string;

    // Settings
    currency: string;
    timezone: string;
    fiscal_year_start: string;
    tax_id: string;
}

function Onboarding() {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        business_name: '',
        business_type: '',
        industry: '',
        registration_number: '',
        phone: '',
        website: '',
        address_street: '',
        address_city: '',
        address_state: '',
        address_postal_code: '',
        address_country: 'India',
        currency: 'INR',
        timezone: 'Asia/Kolkata',
        fiscal_year_start: '04-01',
        tax_id: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateStep = (step: number): boolean => {
        let isValid = true;
        let errorMessage = null;

        if (step === 1) {
            if (!formData.name.trim()) errorMessage = "Full Name is required";
            else if (!formData.email.trim()) errorMessage = "Email is required";
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errorMessage = "Invalid email format";
            else if (formData.password.length < 6) errorMessage = "Password must be at least 6 characters";
            else if (formData.password !== formData.password_confirmation) errorMessage = "Passwords do not match";
        } else if (step === 2) {
            if (!formData.business_name.trim()) errorMessage = "Business Name is required";
            else if (!formData.phone.trim()) errorMessage = "Phone number is required";
            else if (!formData.industry) errorMessage = "Please select an industry";
            else if (!formData.address_country) errorMessage = "Country is required";
        }

        if (errorMessage) {
            setError(errorMessage);
            isValid = false;
        } else {
            setError(null);
        }

        return isValid;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            if (currentStep < 3) {
                setCurrentStep(currentStep + 1);
                window.scrollTo(0, 0);
            }
        }
    };

    const handleBack = () => {
        setError(null);
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            // First create the user account
            const signupResponse = await fetch('/users.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': (window as any).CSRF_TOKEN || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                },
                body: JSON.stringify({
                    user: {
                        name: formData.name,
                        email: formData.email,
                        password: formData.password,
                        password_confirmation: formData.password_confirmation
                    }
                })
            });

            if (!signupResponse.ok) {
                const errorData = await signupResponse.json();
                setError(errorData.error || errorData.errors?.join(', ') || 'Failed to create account');
                setLoading(false);
                return;
            }

            // Then create the firm
            const firmResponse = await fetch('/api/v1/firms', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': (window as any).CSRF_TOKEN || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                },
                body: JSON.stringify({
                    firm: {
                        name: formData.business_name,
                        pincode: formData.address_postal_code, // Changed from postal_code to pincode
                        phone: formData.phone,
                        website: formData.website,
                        address: formData.address_street,
                        city: formData.address_city,
                        state: formData.address_state,
                        country: formData.address_country,
                        industry: formData.industry,

                        // These are not direct columns, we'll put them in settings or handle in controller if we want
                        // But for now, let's send them and update controller to put them in settings
                        settings: {
                            business_type: formData.business_type,
                            registration_number: formData.registration_number,
                            currency: formData.currency,
                            timezone: formData.timezone,
                            fiscal_year_start: formData.fiscal_year_start,
                            tax_id: formData.tax_id
                        }
                    }
                })
            });

            if (firmResponse.ok) {
                const firmData = await firmResponse.json();
                const firmId = firmData.id || firmData.data?.id;

                // Handle logo upload if file selected
                if (logoFile && firmId) {
                    try {
                        const logoData = new FormData();
                        logoData.append('logo', logoFile);
                        await fetch(`/api/v1/firms/${firmId}/upload_logo`, {
                            method: 'POST',
                            body: logoData,
                            headers: {
                                'X-CSRF-Token': (window as any).CSRF_TOKEN || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                            }
                        });
                    } catch (logoErr) {
                        console.error('Logo upload failed:', logoErr);
                        // We continue because firm and user are already created
                    }
                }

                window.location.href = '/';
            } else {
                const errorData = await firmResponse.json();
                setError(errorData.error || 'Failed to create business profile');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl w-full bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col md:flex-row min-h-[600px]">

                {/* Left Side - Visual Guide */}
                <div className="md:w-1/3 bg-slate-900 p-8 text-white relative flex flex-col justify-between">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700 opacity-90"></div>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-16 translate-x-16"></div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-10">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 font-extrabold text-xl shadow-lg">
                                IM
                            </div>
                            <span className="font-bold text-lg tracking-tight">Inventory Mate</span>
                        </div>

                        <div className="space-y-8">
                            <div className={`flex items-start gap-4 transition-opacity duration-300 ${currentStep === 1 ? 'opacity-100' : 'opacity-50'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 ${currentStep === 1 ? 'bg-white text-indigo-600 border-white' : 'border-white/30 text-white'}`}>1</div>
                                <div>
                                    <h4 className="font-bold text-sm">Account Setup</h4>
                                    <p className="text-xs text-indigo-200 mt-1">Secure your admin access</p>
                                </div>
                            </div>
                            <div className={`flex items-start gap-4 transition-opacity duration-300 ${currentStep === 2 ? 'opacity-100' : 'opacity-50'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 ${currentStep === 2 ? 'bg-white text-indigo-600 border-white' : 'border-white/30 text-white'}`}>2</div>
                                <div>
                                    <h4 className="font-bold text-sm">Organization Details</h4>
                                    <p className="text-xs text-indigo-200 mt-1">Tell us about your business</p>
                                </div>
                            </div>
                            <div className={`flex items-start gap-4 transition-opacity duration-300 ${currentStep === 3 ? 'opacity-100' : 'opacity-50'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 ${currentStep === 3 ? 'bg-white text-indigo-600 border-white' : 'border-white/30 text-white'}`}>3</div>
                                <div>
                                    <h4 className="font-bold text-sm">Preferences</h4>
                                    <p className="text-xs text-indigo-200 mt-1">Currency, tax, and formats</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 mt-auto pt-10">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                            <p className="text-xs text-indigo-100 italic">"The most intuitive inventory system needed for modern businesses."</p>
                            <div className="flex gap-1 mt-2 text-amber-300 text-xs">★★★★★</div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Forms */}
                <div className="md:w-2/3 p-8 sm:p-12 relative bg-white">
                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-lg">
                            <div className="flex gap-3">
                                <svg className="w-5 h-5 text-rose-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p className="text-sm text-rose-700 font-medium">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Account Setup */}
                    {currentStep === 1 && (
                        <div className="animate-in fade-in slide-in-from-right-8 duration-500 space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Create your account</h2>
                                <p className="text-slate-500 mt-2 text-sm">Have an account? <Link to="/login" className="text-indigo-600 font-bold hover:underline">Sign in</Link></p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Full Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="name@company.com"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Password</label>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Confirm Password</label>
                                    <input
                                        type="password"
                                        name="password_confirmation"
                                        value={formData.password_confirmation}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Organization Details */}
                    {currentStep === 2 && (
                        <div className="animate-in fade-in slide-in-from-right-8 duration-500 space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Organization Profile</h2>
                                <p className="text-slate-500 mt-2 text-sm">Details about your company</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2 col-span-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Business Name</label>
                                    <input
                                        type="text"
                                        name="business_name"
                                        value={formData.business_name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="Acme Corp"
                                        required
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Business Logo / Profile Picture</label>
                                    <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                                        <div className="relative w-20 h-20 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex items-center justify-center">
                                            {logoPreview ? (
                                                <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-contain" />
                                            ) : (
                                                <div className="text-slate-300">
                                                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <input
                                                type="file"
                                                id="logo-upload"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleLogoChange}
                                            />
                                            <label
                                                htmlFor="logo-upload"
                                                className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer transition-all shadow-sm"
                                            >
                                                Choose Image
                                            </label>
                                            <p className="text-[10px] text-slate-500 font-medium">
                                                JPG, PNG or SVG. Max 2MB.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Business Type</label>
                                    <select
                                        name="business_type"
                                        value={formData.business_type}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                    >
                                        <option value="">Select Type</option>
                                        <option value="retail">Retail</option>
                                        <option value="wholesale">Wholesale</option>
                                        <option value="manufacturing">Manufacturing</option>
                                        <option value="services">Services</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Industry</label>
                                    <select
                                        name="industry"
                                        value={formData.industry}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                    >
                                        <option value="">Select Industry</option>
                                        <option value="electronics">Electronics</option>
                                        <option value="vehicle">Vehicle / Automotive</option>
                                        <option value="grocery">Grocery / Supermarket</option>
                                        <option value="fashion">Fashion / Apparel</option>
                                        <option value="pharmacy">Pharmacy / Medical</option>
                                        <option value="retail">General Retail</option>
                                        <option value="rental">Rental Business</option>
                                        <option value="dairy">Milk & Dairy</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Phone</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="+91 9876543210"
                                        required
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Address</label>
                                    <input
                                        type="text"
                                        name="address_street"
                                        value={formData.address_street}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="Street Address, City, State"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Preferences */}
                    {currentStep === 3 && (
                        <div className="animate-in fade-in slide-in-from-right-8 duration-500 space-y-6">
                            <div>
                                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Final Details</h2>
                                <p className="text-slate-500 mt-2 text-sm">Configure your workspace defaults</p>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Currency</label>
                                        <select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        >
                                            <option value="INR">INR (₹)</option>
                                            <option value="USD">USD ($)</option>
                                            <option value="EUR">EUR (€)</option>
                                            <option value="GBP">GBP (£)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Timezone</label>
                                        <select
                                            name="timezone"
                                            value={formData.timezone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        >
                                            <option value="Asia/Kolkata">IST (Asia/Kolkata)</option>
                                            <option value="America/New_York">EST (New York)</option>
                                            <option value="Europe/London">GMT (London)</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tax ID / GST</label>
                                    <input
                                        type="text"
                                        name="tax_id"
                                        value={formData.tax_id}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="Optional"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Website</label>
                                    <input
                                        type="url"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all font-medium text-slate-800"
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
                        {currentStep > 1 ? (
                            <button
                                onClick={handleBack}
                                className="px-6 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors"
                            >
                                ← Back
                            </button>
                        ) : (
                            <Link to="/" className="px-6 py-3 rounded-xl font-bold text-slate-400 hover:text-slate-600 transition-colors">
                                Cancel
                            </Link>
                        )}

                        <button
                            onClick={currentStep === 3 ? handleSubmit : handleNext}
                            disabled={loading}
                            className={`px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold uppercase tracking-wide hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 flex items-center gap-2 ${loading ? 'opacity-70 cursor-wait' : ''}`}
                        >
                            {loading && (
                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            {currentStep === 3 ? (loading ? 'Creating Project...' : 'Complete Setup') : 'Continue →'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Onboarding;
